// icu formats the ICU MessageFormat subset Kehai's catalogs use (spec 013,
// specs/013-languages/contracts): {name}, {n, plural, =N {…} one {…} other
// {…}} with #, and {x, select, a {…} other {…}}. An apostrophe quotes a
// following brace or apostrophe and is literal anywhere else. The apps'
// Kotlin and the server implement the same subset and pass
// testdata/i18n-format.json. It imports nothing, so Node's tests run it.

export type Args = Record<string, unknown>;

const rules = new Map<string, Intl.PluralRules>();

// pluralCategory picks the CLDR category of a whole number, following
// English for a language the browser has no rules for.
export function pluralCategory(locale: string, n: number): string {
  let r = rules.get(locale);
  if (!r) {
    const known = Intl.PluralRules.supportedLocalesOf([locale]).length > 0;
    r = new Intl.PluralRules(known ? locale : 'en');
    rules.set(locale, r);
  }
  return r.select(Math.abs(Math.trunc(n)));
}

// format writes message for locale with args; number writes the count # stands for.
export function format(message: string, locale: string, args: Args, number: (n: number) => string = String): string {
  let i = 0;
  const s = message;

  function text(count: number | null, stopAtBrace: boolean): string {
    let out = '';
    while (i < s.length) {
      const c = s[i];
      if (c === "'") {
        const next = s[i + 1];
        if (next === "'") {
          out += "'";
          i += 2;
        } else if (next === '{' || next === '}' || (count !== null && next === '#')) {
          i++;
          while (i < s.length) {
            if (s[i] === "'") {
              if (s[i + 1] === "'") {
                out += "'";
                i += 2;
                continue;
              }
              i++;
              break;
            }
            out += s[i];
            i++;
          }
        } else {
          out += "'";
          i++;
        }
      } else if (c === '{') {
        out += argument(count);
      } else if (c === '}' && stopAtBrace) {
        return out;
      } else if (c === '#' && count !== null) {
        out += number(count);
        i++;
      } else {
        out += c;
        i++;
      }
    }
    return out;
  }

  function argument(count: number | null): string {
    const start = i;
    i++;
    const name = word();
    space();
    if (s[i] === '}') {
      i++;
      if (!(name in args)) return s.slice(start, i);
      const v = args[name];
      return v === null || v === undefined ? '' : String(v);
    }
    if (s[i] !== ',') return skip(start);
    i++;
    space();
    const kind = word();
    space();
    if (s[i] === ',') i++;
    const value = args[name];
    if (kind === 'plural') {
      const n = typeof value === 'number' ? value : null;
      const found = branches(n ?? count);
      const category = n === null ? 'other' : pluralCategory(locale, n);
      return (n !== null ? found.get(`=${n}`) : undefined) ?? found.get(category) ?? found.get('other') ?? '';
    }
    if (kind === 'select') {
      const found = branches(count);
      return found.get(String(value)) ?? found.get('other') ?? '';
    }
    return skip(start);
  }

  function branches(count: number | null): Map<string, string> {
    const out = new Map<string, string>();
    while (i < s.length) {
      space();
      if (i >= s.length) break;
      if (s[i] === '}') {
        i++;
        break;
      }
      const start = i;
      while (i < s.length && !/\s/.test(s[i]) && s[i] !== '{') i++;
      const key = s.slice(start, i);
      space();
      if (s[i] !== '{') break;
      i++;
      out.set(key, text(count, true));
      if (s[i] === '}') i++;
    }
    return out;
  }

  function word(): string {
    space();
    const start = i;
    while (i < s.length && /[\p{L}\p{N}_]/u.test(s[i])) i++;
    return s.slice(start, i);
  }

  function space() {
    while (i < s.length && /\s/.test(s[i])) i++;
  }

  function skip(start: number): string {
    let depth = 1;
    while (i < s.length && depth > 0) {
      if (s[i] === '{') depth++;
      else if (s[i] === '}') depth--;
      i++;
    }
    return s.slice(start, i);
  }

  return text(null, false);
}

// argumentNames lists every argument a message uses, in the order they first appear.
export function argumentNames(message: string): string[] {
  const names: string[] = [];
  const inArgument: boolean[] = [];
  let quoted = false;
  for (let i = 0; i < message.length; i++) {
    const c = message[i];
    if (c === "'") {
      const next = message[i + 1];
      if (next === "'") {
        i++;
        continue;
      }
      if (quoted || next === '{' || next === '}') quoted = !quoted;
      continue;
    }
    if (quoted) continue;
    if (c === '{') {
      const argument = inArgument[inArgument.length - 1] !== true;
      inArgument.push(argument);
      if (argument) {
        const m = /^\{\s*([^,}\s]+)/.exec(message.slice(i));
        if (m && !names.includes(m[1])) names.push(m[1]);
      }
    } else if (c === '}' && inArgument.length > 0) {
      inArgument.pop();
    }
  }
  return names;
}

const PLAIN = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
const FANCY = 'åƀçđéƒĝĥîĵķļɱñöþǫŕšţûṽŵẋýžÅƁÇĐÉƑĜĤÎĴĶĻṀÑÖÞǪŔŠŢÛṼŴẊÝŽ';

// pseudo writes the pseudo-language en-XA from an English message (013
// FR-022): letters accented, about 40% padding, and brackets, with
// arguments and plural or select syntax left alone.
export function pseudo(message: string): string {
  let out = '';
  let letters = 0;
  const inArgument: boolean[] = [];
  for (const c of message) {
    if (c === '{') {
      inArgument.push(inArgument[inArgument.length - 1] !== true);
      out += c;
    } else if (c === '}') {
      inArgument.pop();
      out += c;
    } else if (inArgument[inArgument.length - 1] === true || c === "'") {
      out += c;
    } else {
      const k = PLAIN.indexOf(c);
      out += k >= 0 ? [...FANCY][k] : c;
      if (/\p{L}/u.test(c)) letters++;
    }
  }
  const pad = Math.floor((letters * 2 + 4) / 5);
  return `[${out}${pad > 0 ? ' ' + '~'.repeat(pad) : ''}]`;
}
